/* eslint-disable spaced-comment */
/* eslint-disable capitalized-comments */
import type { DeepPartial } from '@tryiris-ai/common.protojs';
import type { Device } from '@studio/models/device';
import { isPtz } from '@studio/models/ptz';
import type { PTZ } from '@studio/models/ptz';
import { RampType } from '@tryiris-ai/common.protojs/agent/v1/agent';
import type { CommandDevice } from '@tryiris-ai/common.protojs/agent/v1/agent';
import type { FastFrame, MoveTo } from './types.js';
import { locateProxy } from '#~/agent/index.js';
import type { OmitSyncProps } from '#~/api/index.js';
import { resolveDeviceAnalyticsContext } from '#~/analytics/device-context.js';
import { EVENT_CATEGORIES, EVENT_NAMES } from '#~/analytics/constants/index.js';
import { track } from '#~/analytics/track.js';
import type { AnalyticsEvent } from '#~/analytics/types/index.js';
import { Range } from '#~/control/movement/index.js';

const fastFrame = (
  deviceId: Device['id'],
  position: FastFrame,
  cine: boolean
): OmitSyncProps<Pick<CommandDevice, 'fastFrame'>> => ({
  fastFrame: {
    centerX: position.center.x,
    centerY: position.center.y,
    id: deviceId,
    rampType: RampType.RAMP_TYPE_EXPONENTIAL,
    scale: position.scale ?? 0,
    cine
  }
});

const setPosition = (
  deviceId: Device['id'],
  position: PTZ
): Pick<DeepPartial<CommandDevice>, 'moveTo'> => {
  const { pan, tilt, zoom } = position;

  const speed = Range.max;

  return {
    moveTo: {
      id: deviceId,
      panTilt: {
        pan: {
          position: pan,
          speed
        },
        tilt: {
          position: tilt,
          speed
        }
      },
      zoom: {
        zoom: {
          position: zoom,
          speed
        }
      }
    }
  };
};

/**
 * Move the device to the specified position.
 *
 * @param deviceId - The ID of the device to be moved.
 * @param position - Position.
 */
export const moveTo: MoveTo = async (deviceId, position, cine, options) => {
  const proxy = locateProxy(deviceId);
  const deviceAnalyticsContext = resolveDeviceAnalyticsContext(deviceId) ?? { device_id: deviceId };
  const toolUsed = options?.toolUsed ?? 'direct_move';

  if (!proxy) {
    return false;
  }

  void track({
    category: EVENT_CATEGORIES.DEVICE,
    name: EVENT_NAMES.DEVICE_MOVE_DIRECT,
    properties: {
      ...deviceAnalyticsContext,
      movement_type: isPtz(position) ? 'ptz' : 'frame',
      shot_type: cine ? 'cine' : 'standard',
      tool_used: toolUsed
    }
  } as AnalyticsEvent);

  await proxy.mutate(
    {
      command: {
        device: isPtz(position)
          ? setPosition(deviceId, position)
          : fastFrame(deviceId, position, cine)
      }
    },
    {}
  );
  return true;
};
